import { useState } from "react";
import {
  api_private_post,
  formatStringAmountCredits,
  showAlert,
} from "script";

const REASONS = [
  { value: "interac_deposit", label: "Interac e-Transfer deposit" },
  { value: "manual", label: "Manual" },
];

function accountTypeLabel(accountType) {
  return accountType === "real" ? "Real currency" : "Credits";
}

function formatAccountAmount(amount, accountType) {
  return formatStringAmountCredits(amount, accountType);
}

export default function AdminBalanceTab() {
  const [userId, setUserId] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [amount, setAmount] = useState("");
  const [operation, setOperation] = useState("add");
  const [reason, setReason] = useState("interac_deposit");
  const [target, setTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  const clearConfirmation = () => setTarget(null);
  const signedAmount =
    operation === "remove"
      ? -Math.abs(Number(amount))
      : Math.abs(Number(amount));

  const searchUsers = (event) => {
    event.preventDefault();
    const query = searchQuery.trim();
    if (query.length < 2) {
      showAlert("Enter at least two characters of a name or email.", "danger");
      return;
    }

    setSearching(true);
    api_private_post(
      "/admin/account/search",
      { query },
      (data) => {
        setSearching(false);
        if (!data?.success) {
          showAlert(data?.error || "User search failed.", "danger");
          return;
        }
        setSearchResults(data.users || []);
      },
      () => {
        setSearching(false);
        showAlert("Unable to search users.", "danger");
      },
    );
  };

  const selectSearchResult = (user) => {
    setUserId(user.user_id);
    setSearchQuery(`${user.display_name} (${user.email})`);
    setSearchResults([]);
    clearConfirmation();
  };

  const lookupUser = (event) => {
    event.preventDefault();
    if (
      !userId.trim() ||
      !Number.isFinite(signedAmount) ||
      signedAmount === 0
    ) {
      showAlert("Enter a user ID and an amount greater than zero.", "danger");
      return;
    }
    if (reason === "interac_deposit" && operation === "remove") {
      showAlert(
        "Interac deposits can only add funds. Use Manual to remove funds.",
        "danger",
      );
      return;
    }

    setBusy(true);
    api_private_post(
      "/admin/account/lookup",
      { user_id: userId.trim() },
      (data) => {
        setBusy(false);
        if (!data?.success) {
          showAlert(data?.error || "User not found.", "danger");
          return;
        }
        setTarget(data.user);
      },
      () => {
        setBusy(false);
        showAlert("Unable to look up this user.", "danger");
      },
    );
  };

  const confirmChange = () => {
    if (!target || busy) return;
    setBusy(true);
    api_private_post(
      "/admin/account/change-balance",
      {
        user_id: target.user_id,
        amount: signedAmount,
        reason,
        confirmed_email: target.email,
        confirmed_account_type: target.account_type,
      },
      (data) => {
        setBusy(false);
        if (!data?.success) {
          showAlert(data?.error || "Balance change failed.", "danger");
          return;
        }
        showAlert(
          `Balance updated. Transaction ${data.transaction_id}.`,
          "success",
        );
        setTarget(data.user);
        setAmount("");
      },
      () => {
        setBusy(false);
        showAlert("Balance change failed.", "danger");
      },
    );
  };

  return (
    <div className="pay_method__paymethod p-4 p-lg-6 p2-bg rounded-8">
      <div className="pay_method__paymethod-title mb-5 mb-md-6">
        <h5 className="n10-color">Change balances</h5>
      </div>

      <form className="mb-6" onSubmit={searchUsers}>
        <label className="mb-2" htmlFor="admin-user-search">
          Find user by name or email
        </label>
        <div className="d-flex gap-3 align-items-stretch">
          <input
            id="admin-user-search"
            className="n11-bg rounded-8 w-100"
            value={searchQuery}
            onChange={(event) => {
              setSearchQuery(event.target.value);
              setSearchResults([]);
            }}
            placeholder="Name or email"
          />
          <button
            className="cmn-btn px-5 py-3 text-nowrap"
            type="submit"
            disabled={searching}
          >
            {searching ? "Searching..." : "Search"}
          </button>
        </div>
      </form>

      {searchResults.length > 0 && (
        <div
          className="admin-user-search-results n11-bg rounded-8 mb-6"
          role="list"
          aria-label="User search results"
        >
          {searchResults.map((user) => (
            <button
              key={user.user_id}
              type="button"
              role="listitem"
              className="admin-user-search-result w-100 p-4 text-start"
              onClick={() => selectSearchResult(user)}
            >
              <strong>{user.display_name}</strong>
              <span>{user.email}</span>
              <small>
                {accountTypeLabel(user.account_type)} · Balance{" "}
                {formatAccountAmount(user.balance, user.account_type)}
              </small>
            </button>
          ))}
        </div>
      )}

      <form onSubmit={lookupUser}>
        <div className="row g-4 mb-5">
          <div className="col-md-6">
            <label className="mb-2" htmlFor="admin-balance-user-id">
              User ID
            </label>
            <input
              id="admin-balance-user-id"
              className="n11-bg rounded-8 w-100"
              value={userId}
              onChange={(event) => {
                setUserId(event.target.value);
                clearConfirmation();
              }}
              required
            />
          </div>
          <div className="col-md-6">
            <label className="mb-2" htmlFor="admin-balance-operation">
              Operation
            </label>
            <select
              id="admin-balance-operation"
              className="admin-balance-select n11-bg rounded-8 w-100 p-4"
              value={operation}
              onChange={(event) => {
                const nextOperation = event.target.value;
                setOperation(nextOperation);
                if (nextOperation === "remove") setReason("manual");
                clearConfirmation();
              }}
            >
              <option value="add">Add funds</option>
              <option value="remove">Remove funds</option>
            </select>
          </div>
          <div className="col-md-6">
            <label className="mb-2" htmlFor="admin-balance-amount">
              Amount
            </label>
            <input
              id="admin-balance-amount"
              className="n11-bg rounded-8 w-100"
              type="number"
              min="0.01"
              step="0.01"
              value={amount}
              onChange={(event) => {
                setAmount(event.target.value);
                clearConfirmation();
              }}
              required
            />
          </div>
          <div className="col-md-6">
            <label className="mb-2" htmlFor="admin-balance-reason">
              Reason
            </label>
            <select
              id="admin-balance-reason"
              className="admin-balance-select n11-bg rounded-8 w-100 p-4"
              value={reason}
              onChange={(event) => {
                setReason(event.target.value);
                clearConfirmation();
              }}
            >
              {REASONS.map((option) => (
                <option
                  key={option.value}
                  value={option.value}
                  disabled={
                    operation === "remove" && option.value === "interac_deposit"
                  }
                >
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <button className="cmn-btn px-5 py-3" type="submit" disabled={busy}>
          Review change
        </button>
      </form>

      {target && amount && (
        <div className="p1-bg rounded-3 p-5 mt-6">
          <h6 className="n10-color mb-4">Confirm account</h6>
          <dl className="row mb-5">
            <dt className="col-sm-4">Name</dt>
            <dd className="col-sm-8">{target.display_name}</dd>
            <dt className="col-sm-4">Email</dt>
            <dd className="col-sm-8">{target.email}</dd>
            <dt className="col-sm-4">Account</dt>
            <dd className="col-sm-8">
              {accountTypeLabel(target.account_type)}
            </dd>
            <dt className="col-sm-4">Current balance</dt>
            <dd className="col-sm-8">
              {formatAccountAmount(target.balance, target.account_type)}
            </dd>
            <dt className="col-sm-4">Change</dt>
            <dd className="col-sm-8">
              {formatAccountAmount(signedAmount, target.account_type)}
            </dd>
            <dt className="col-sm-4">New balance</dt>
            <dd className="col-sm-8">
              {formatAccountAmount(
                Number(target.balance) + signedAmount,
                target.account_type,
              )}
            </dd>
          </dl>
          <div className="d-flex gap-3 flex-wrap">
            <button
              className="cmn-btn px-5 py-3"
              type="button"
              disabled={busy}
              onClick={confirmChange}
            >
              Confirm balance change
            </button>
            <button
              className="bj-action-btn bj-ghost px-5 py-3"
              type="button"
              disabled={busy}
              onClick={clearConfirmation}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <style>{`
                .admin-balance-select,
                .admin-balance-select option {
                    color: #fff;
                    background-color: rgb(var(--n11));
                }
                .admin-user-search-results {
                    max-height: 320px;
                    overflow-y: auto;
                    border: 1px solid rgba(255, 255, 255, 0.12);
                }
                .admin-user-search-result {
                    display: grid;
                    gap: 4px;
                    color: #fff;
                    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
                }
                .admin-user-search-result:last-child {
                    border-bottom: 0;
                }
                .admin-user-search-result:hover,
                .admin-user-search-result:focus-visible {
                    background: rgba(255, 255, 255, 0.08);
                }
                .admin-user-search-result span,
                .admin-user-search-result small {
                    color: rgba(255, 255, 255, 0.75);
                }
            `}</style>
    </div>
  );
}
